import { FC } from "react";
import { useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import {
  capitalize,
  currencySignSelector,
  isEqualNumberArraysAnyOrder,
  IStationPriceAdditionDto,
  Log,
  safeNumber,
  safeParseNumericStringOrUndefined,
} from "@ryde/shared";

import {
  ColorIconButton,
  MultiSelectBoundInputByControl,
  NumberInputByControl,
} from "../../../controls";
import { IStationAdditionItemForm } from "./station-addition.models";
import { StationAdditionEditItemStyled } from "./station-addition.styled";

export const StationAdditionItemEdit: FC<IStationAdditionItemForm> = ({
  index,
  value,
  onCancel,
  setValue: setValueParentForm,
  getValues: getValuesParentForm,
  fieldName,
  isNew,
}) => {
  const { t } = useTranslation();
  const { watch } = useFormContext();
  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = useForm<Partial<IStationPriceAdditionDto>>({
    defaultValues: value,
    mode: "all",
  });

  const currencySign = useRecoilValue(currencySignSelector);

  const sourceAreaWatchedId = watch("sourceAreaId");
  const destinationAreaWatchedId = watch("destinationAreaId");

  const excludeAreaIds = [sourceAreaWatchedId, destinationAreaWatchedId].filter(
    Boolean,
  ) as number[];

  const updateValuesHandler = handleSubmit(data => {
    const targetField = `${fieldName ?? ""}.${index || 0}`;

    if (safeParseNumericStringOrUndefined(data.price) === undefined) {
      Log.warnAndToast(t("errors.invalidNumber"));
      return;
    }

    const dataNormalized: IStationPriceAdditionDto = {
      price: safeNumber(data.price),
      areaIds: data.areaIds?.map(areaId => Number(areaId)) || [],
      startingFromStationIndex: data.startingFromStationIndex,
    };

    const existingPriceItems: IStationPriceAdditionDto[] =
      getValuesParentForm?.(fieldName);

    const priceItemsExcludeCurrent =
      typeof index === "number" && existingPriceItems?.length
        ? existingPriceItems.toSpliced(index, 1)
        : existingPriceItems || [];

    const combinationExists = priceItemsExcludeCurrent.some(priceItem => {
      const isSameAreaIds = isEqualNumberArraysAnyOrder(
        priceItem.areaIds,
        dataNormalized.areaIds,
      );
      const isSameStartingFromStationIndex =
        safeNumber(priceItem.startingFromStationIndex) ===
        safeNumber(dataNormalized.startingFromStationIndex);

      return isSameAreaIds && isSameStartingFromStationIndex;
    });

    if (isNew && combinationExists) {
      Log.warnAndToast(t("general.ITEM_ALREADY_EXISTS_MESSAGE"));
      return;
    }

    Log.debug(
      `Set value for availability item target field:  ${targetField}`,
      data,
    );
    setValueParentForm?.(targetField, dataNormalized, {
      shouldValidate: true,
      shouldDirty: true,
    });
    onCancel?.();
  });

  return (
    <StationAdditionEditItemStyled>
      <NumberInputByControl
        name="startingFromStationIndex"
        control={control}
        formLabel={t("priceRate.formLabel.startingFromStationIndex")}
        formAttributes={{ numberInput: { minValue: 0 } }}
      />

      <MultiSelectBoundInputByControl
        name={"areaIds"}
        control={control}
        formLabel={capitalize(t("area.plural"))}
        formAttributes={{
          dropItemsSource: "area",
          customDropdownItemsFilter: excludeAreaIds?.length
            ? item => {
                if (!item?.id) {
                  return false;
                }
                const excludeAreaIdsSet = new Set(excludeAreaIds);
                return !excludeAreaIdsSet.has(item.id as number);
              }
            : undefined,
        }}
      />

      <div className="buttons_edit_view">
        <NumberInputByControl
          name={"price"}
          control={control}
          formLabel={`${currencySign} ${t("general.PRICE")}`}
          formAttributes={{ numberInput: { minValue: 0 } }}
          formValidation={{
            type: "number",
            rules: [
              {
                validation: "required",
              },
            ],
          }}
        />
      </div>

      <div>
        <ColorIconButton
          iconType="CHECK"
          edge="end"
          aria-label="save"
          disabled={!isValid}
          onClick={updateValuesHandler}
        />
        <ColorIconButton
          iconType="CANCEL"
          edge="end"
          aria-label="cancel"
          onClick={() => onCancel?.()}
        />
      </div>
    </StationAdditionEditItemStyled>
  );
};
